import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, DollarSign, Store, Image as ImageIcon, Plus, Search, X, Camera, Scan } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import BarcodeScanner from '@/components/BarcodeScanner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? <Marker position={position}></Marker> : null;
}

export default function ReportPrice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedCity, token, refreshUser } = useContext(AuthContext);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    brand: '',
    size: '',
    store_name: '',
    price: '',
    address: '',
    is_promo: false,
    promo_label: '',
    is_combo: false,
    deal_quantity: '',
    deal_price: '',
    deal_label: '',
    tags_input: '',
    tags: [],
  });
  const [position, setPosition] = useState(
    selectedCity ? [selectedCity.lat, selectedCity.lng] : [-26.2041, 28.0473]
  );
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [promoValidTo, setPromoValidTo] = useState('');

  const compressImageToDataUrl = (file, opts = {}) => {
    const {
      maxDimension = 1280,
      quality = 0.78,
      mimeType = 'image/jpeg',
    } = opts;

    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          try {
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            if (!w || !h) {
              URL.revokeObjectURL(url);
              reject(new Error('Invalid image dimensions'));
              return;
            }

            const scale = Math.min(1, maxDimension / Math.max(w, h));
            const targetW = Math.max(1, Math.round(w * scale));
            const targetH = Math.max(1, Math.round(h * scale));

            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(url);
              reject(new Error('Canvas not supported'));
              return;
            }

            ctx.drawImage(img, 0, 0, targetW, targetH);
            const dataUrl = canvas.toDataURL(mimeType, quality);
            URL.revokeObjectURL(url);
            resolve(dataUrl);
          } catch (err) {
            URL.revokeObjectURL(url);
            reject(err);
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image'));
        };

        img.src = url;
      } catch (err) {
        reject(err);
      }
    });
  };

  const addTagsFromRaw = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    const parts = trimmed
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (parts.length === 0) return;

    setFormData((prev) => {
      const existing = new Set((prev.tags || []).map((t) => String(t).toLowerCase()));
      const next = [...(prev.tags || [])];
      parts.forEach((p) => {
        const key = p.toLowerCase();
        if (!existing.has(key)) {
          next.push(p);
          existing.add(key);
        }
      });
      return { ...prev, tags: next, tags_input: '' };
    });
  };

  useEffect(() => {
    if (!token) {
      navigate('/auth');
    }
    if (!selectedCity) {
      navigate('/');
    }
  }, [token, selectedCity, navigate]);

  useEffect(() => {
    // Prefill from query params when coming from Results/Map
    const productName = searchParams.get('product_name');
    const brand = searchParams.get('brand');
    const size = searchParams.get('size');
    const storeName = searchParams.get('store_name');
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');

    if (productName || brand || size || storeName) {
      setFormData((prev) => ({
        ...prev,
        product_name: productName || prev.product_name,
        brand: brand || prev.brand,
        size: size || prev.size,
        store_name: storeName || prev.store_name,
      }));
    }

    if (lat && lng) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        setPosition([latNum, lngNum]);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const searchAddress = async () => {
      if (addressSearch.length < 3) {
        setAddressSuggestions([]);
        return;
      }

      setSearchingAddress(true);
      try {
        // First try backend Google Places proxy (avoids CORS and hides API key)
        const params = new URLSearchParams({
          q: addressSearch,
          city: selectedCity?.name || '',
        });
        const placesResponse = await fetch(`${API}/places/search?${params.toString()}`);
        if (placesResponse.ok) {
          const placesData = await placesResponse.json();
          if (Array.isArray(placesData) && placesData.length > 0) {
            setAddressSuggestions(placesData);
            return;
          }
        }

        // If backend returns nothing, fall back to Nominatim (with broader SA search)
        const primaryResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            addressSearch + ', ' + selectedCity?.name + ', South Africa'
          )}&limit=5`
        );
        let data = await primaryResponse.json();

        if (!Array.isArray(data) || data.length === 0) {
          const fallbackResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              addressSearch + ', South Africa'
            )}&limit=5`
          );
          data = await fallbackResponse.json();
        }

        setAddressSuggestions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to search address', error);
      } finally {
        setSearchingAddress(false);
      }
    };

    const debounceTimer = setTimeout(searchAddress, 500);
    return () => clearTimeout(debounceTimer);
  }, [addressSearch, selectedCity]);

  const handleAddressSelect = (suggestion) => {
    const fullAddress = suggestion.display_name;
    const firstPart = fullAddress.split(',')[0].trim();

    // If Google Places provided a business name, prefer that as the store name.
    if (suggestion.name) {
      setFormData((prev) => ({
        ...prev,
        store_name: suggestion.name,
        address: fullAddress,
      }));
    } else {
      // If the first part of the address looks like a street number ("67 Rosmead Ave"),
      // prefer the user's search text as the store name (e.g. "rosmead spar").
      const startsWithNumber = /^\d/.test(firstPart);
      const typed = addressSearch.trim();
      const storeName = startsWithNumber && typed ? typed : firstPart;

      setFormData((prev) => ({
        ...prev,
        store_name: storeName,
        address: fullAddress,
      }));
    }
    setPosition([parseFloat(suggestion.lat), parseFloat(suggestion.lon)]);
    setAddressSearch('');
    setAddressSuggestions([]);
    
    toast.success('Store location selected!');
  };

  const addImagesFromFiles = (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;

    const remaining = Math.max(0, 3 - uploadedImages.length);
    if (remaining <= 0) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    const sliced = list.slice(0, remaining);
    if (sliced.length < list.length) {
      toast.message(`Only ${remaining} more image${remaining === 1 ? '' : 's'} allowed`);
    }

    setUploadingImage(true);

    (async () => {
      try {
        for (const file of sliced) {
          if (file.size > 5 * 1024 * 1024) {
            toast.error(`${file.name} is too large. Max 5MB per image.`);
            continue;
          }

          // Resize/compress to speed up submit and keep DB smaller.
          const compressed = await compressImageToDataUrl(file, {
            maxDimension: 1280,
            quality: 0.78,
            mimeType: 'image/jpeg',
          });

          setUploadedImages((prev) => [...prev, compressed]);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to process image');
      } finally {
        setUploadingImage(false);
      }
    })();
  };

  const handleImageUpload = (e) => {
    addImagesFromFiles(e?.target?.files);
    // Allow re-selecting the same photo again
    if (e?.target) e.target.value = '';
  };

  const openCamera = () => {
    if (cameraInputRef.current) cameraInputRef.current.click();
  };

  const openGallery = () => {
    if (galleryInputRef.current) galleryInputRef.current.click();
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate combo: if checked, need deal_quantity and deal_price
    if (formData.is_combo) {
      const qty = parseInt(formData.deal_quantity, 10);
      const total = parseFloat(formData.deal_price);
      if (!qty || qty < 2) {
        toast.error('Combo quantity must be at least 2');
        return;
      }
      if (!total || total <= 0) {
        toast.error('Combo total price must be positive');
        return;
      }
    }

    // Validate promo date (valid until must be today or later, since valid_from is now)
    if (formData.is_promo && promoValidTo) {
      const d = new Date(promoValidTo);
      const now = new Date();
      if (!Number.isNaN(d.getTime())) {
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (d < startOfToday) {
          toast.error('Promo "valid until" cannot be in the past');
          return;
        }
      }
    }

    // Validate minimum 2 photos
    if (uploadedImages.length < 2) {
      toast.error('Please upload at least 2 photos of the product');
      return;
    }

    setLoading(true);

    try {
      let validToIso = null;
      if (promoValidTo) {
        const d = new Date(promoValidTo);
        if (!Number.isNaN(d.getTime())) {
          validToIso = d.toISOString();
        }
      }

      const isCombo = formData.is_combo && formData.deal_quantity && formData.deal_price;
      const reportData = {
        ...formData,
        tags: (formData.tags || []).map((t) => t.trim()).filter(Boolean),
        price: isCombo ? parseFloat(formData.deal_price) : parseFloat(formData.price),
        city: selectedCity.id,
        latitude: position[0],
        longitude: position[1],
        images: uploadedImages,
        valid_from: formData.is_promo ? new Date().toISOString() : null,
        valid_to: formData.is_promo ? validToIso : null,
        deal_quantity: isCombo ? parseInt(formData.deal_quantity, 10) : null,
        deal_price: isCombo ? parseFloat(formData.deal_price) : null,
        deal_label: isCombo && formData.deal_label ? formData.deal_label : null,
      };

      const response = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Report submitted! You earned ${data.points_earned} points! 🎉`);
        refreshUser();
        navigate('/search');
      } else {
        toast.error(data.detail || 'Failed to submit report');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeSuccess = (productInfo) => {
    setScannedProduct(productInfo);
    setFormData((prev) => ({
      ...prev,
      product_name: productInfo.name || prev.product_name,
      brand: productInfo.brand || prev.brand,
      size: productInfo.size || prev.size,
    }));
    
    if (productInfo.name) {
      toast.success('Product details auto-filled from barcode!');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/search')}
            variant="ghost"
            size="icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-heading font-bold text-primary">Report Price</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Help the Community
            </h2>
            <p className="text-muted-foreground">Report a price and earn points!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Barcode Scanner Button */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-2xl p-6 border-2 border-accent/20">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold text-foreground flex items-center gap-2">
                    <Scan className="w-5 h-5 text-accent" />
                    Quick Scan
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scan barcode to auto-fill product details
                  </p>
                </div>
                <Camera className="w-10 h-10 text-accent/40" />
              </div>
              <Button
                type="button"
                onClick={() => setShowBarcodeScanner(true)}
                className="w-full rounded-full bg-accent hover:bg-accent/90"
                data-testid="scan-barcode-button"
              >
                <Scan className="w-5 h-5 mr-2" />
                Scan Product Barcode
              </Button>
            </div>

            {scannedProduct && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <p className="text-sm font-medium text-primary mb-2">✓ Barcode Scanned</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  {scannedProduct.name && <p>Product: {scannedProduct.name}</p>}
                  {scannedProduct.brand && <p>Brand: {scannedProduct.brand}</p>}
                  {scannedProduct.size && <p>Size: {scannedProduct.size}</p>}
                  <p className="text-primary font-mono">Barcode: {scannedProduct.barcode}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="product_name" className="text-sm font-medium">
                Product Type * <span className="text-xs text-muted-foreground">(e.g., Milk, Bread, Eggs)</span>
              </Label>
              <div className="relative">
                <ImageIcon className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                <Input
                  id="product_name"
                  data-testid="product-name-input"
                  placeholder="e.g., Milk"
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Tags (optional)
                <span className="text-xs text-muted-foreground ml-1">
                  e.g. chips, snack, crisps
                </span>
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid="tags-input"
                  placeholder="Add tags (comma separated) then tap Add - to improve search results"
                  value={formData.tags_input}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, tags_input: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    // Desktop convenience: Enter to add.
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTagsFromRaw(formData.tags_input);
                    }
                    // Mobile convenience: comma adds immediately.
                    if (e.key === ',') {
                      const v = e.currentTarget.value || '';
                      const beforeComma = v.replace(/,$/, '');
                      if (beforeComma.trim()) {
                        e.preventDefault();
                        addTagsFromRaw(beforeComma);
                      }
                    }
                  }}
                  onBlur={() => {
                    // If the user typed something and tapped Next/dismissed keyboard, add it.
                    if (formData.tags_input.trim()) addTagsFromRaw(formData.tags_input);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => addTagsFromRaw(formData.tags_input)}
                  disabled={!formData.tags_input.trim()}
                >
                  Add
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {formData.tags.map((tag, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          tags: prev.tags.filter((_, i) => i !== idx),
                        }))
                      }
                      className="px-2 py-0.5 rounded-full bg-muted text-xs flex items-center gap-1 hover:bg-muted/80"
                    >
                      <span>{tag}</span>
                      <X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand" className="text-sm font-medium">
                  Brand <span className="text-xs text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="brand"
                  data-testid="brand-input"
                  placeholder="e.g., Clover, Darling"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="size" className="text-sm font-medium">
                  Size/Weight <span className="text-xs text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="size"
                  data-testid="size-input"
                  placeholder="e.g., 2L, 700g"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                Multi-buy deal (optional)
              </Label>
              <div className="flex flex-col gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={formData.is_combo}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_combo: e.target.checked }))
                    }
                  />
                  This is a combo deal (e.g. 2 for R25)
                </label>
                {formData.is_combo && (
                  <div className="flex flex-wrap items-end gap-3 pl-6 border-l-2 border-primary/30">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={2}
                        placeholder="Qty"
                        value={formData.deal_quantity}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, deal_quantity: e.target.value }))
                        }
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">for</span>
                    </div>
                    <div className="relative flex-1 min-w-[100px]">
                      <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">
                        R
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Total (e.g. 25)"
                        value={formData.deal_price}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, deal_price: e.target.value }))
                        }
                        className="pl-8"
                      />
                    </div>
                    <Input
                      placeholder="Label (e.g. 2 for R25)"
                      value={formData.deal_label}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, deal_label: e.target.value }))
                      }
                      className="text-sm max-w-[140px]"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price" className="text-sm font-medium">
                {formData.is_combo ? 'Single-item price (optional, for reference)' : 'Price (ZAR) *'}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">
                  R
                </span>
                <Input
                  id="price"
                  data-testid="price-input"
                  type="number"
                  step="0.01"
                  placeholder={formData.is_combo ? 'e.g., 14.00' : 'e.g., 25.99'}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="pl-8"
                  required={!formData.is_combo}
                />
              </div>
              {formData.is_combo && formData.deal_quantity && formData.deal_price && (
                <p className="text-xs text-muted-foreground">
                  ≈ R{(Number(formData.deal_price) / Number(formData.deal_quantity)).toFixed(2)} each
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                Promotion (optional)
              </Label>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={formData.is_promo}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_promo: e.target.checked }))
                    }
                  />
                  This is a promotional price
                </label>
                {formData.is_promo && (
                  <div className="flex flex-1 flex-col sm:flex-row gap-3">
                    <Input
                      placeholder="Promo label (e.g., Weekend Special)"
                      value={formData.promo_label}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, promo_label: e.target.value }))
                      }
                      className="text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        Valid until
                      </span>
                      <Input
                        type="date"
                        value={promoValidTo}
                        onChange={(e) => setPromoValidTo(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Address Search */}
            <div className="space-y-2">
              <Label htmlFor="address_search" className="text-sm font-medium">
                Search Store Address *
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                <Input
                  id="address_search"
                  data-testid="address-search-input"
                  placeholder="Start typing store address or name..."
                  value={addressSearch}
                  onChange={(e) => setAddressSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              {searchingAddress && (
                <p className="text-sm text-muted-foreground">Searching...</p>
              )}
              {addressSuggestions.length > 0 && (
                <div className="bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {addressSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      data-testid={`address-suggestion-${idx}`}
                      onClick={() => handleAddressSelect(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm text-foreground">
                            {suggestion.display_name.split(',')[0]}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {suggestion.display_name}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="store_name" className="text-sm font-medium">
                Store Name *
              </Label>
              <div className="relative">
                <Store className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                <Input
                  id="store_name"
                  data-testid="store-name-input"
                  placeholder="Auto-filled from address search"
                  value={formData.store_name}
                  onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-sm font-medium">
                Full Address *
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                <Input
                  id="address"
                  data-testid="address-input"
                  placeholder="Auto-filled from address search"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Store Location on Map
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                Location auto-updates from address search or click to adjust
              </p>
              <div className="rounded-2xl overflow-hidden border-2 border-border h-64" data-testid="location-map">
                <MapContainer
                  center={position}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  <LocationMarker position={position} setPosition={setPosition} />
                </MapContainer>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-primary" />
                Product Photos (Minimum 2 required) *
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                Upload at least 2 photos: product label + price tag
              </p>
              
              {uploadedImages.length < 3 && (
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    ref={galleryInputRef}
                    data-testid="image-upload-input"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-camera"
                    ref={cameraInputRef}
                    data-testid="image-camera-input"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="default"
                      className="rounded-full"
                      onClick={openCamera}
                      disabled={uploadingImage}
                      data-testid="take-photo-button"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Take photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={openGallery}
                      disabled={uploadingImage}
                      data-testid="choose-photos-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Choose from gallery
                    </Button>
                  </div>
                  <label
                    htmlFor="image-upload"
                    className="hidden"
                  >
                    <Plus className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {uploadingImage ? 'Uploading...' : 'Click to upload images'}
                    </span>
                  </label>
                </div>
              )}

              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {uploadedImages.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {uploadedImages.length}/3 images uploaded
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                data-testid="cancel-button"
                type="button"
                onClick={() => navigate('/search')}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-report-button"
                type="submit"
                className="flex-1 rounded-full bg-accent hover:bg-accent/90"
                disabled={loading}
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </main>

      {showBarcodeScanner && (
        <BarcodeScanner
          onScanSuccess={handleBarcodeSuccess}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}
    </div>
  );
}
